import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAckPlusCelebration,
  useFlags,
  usePass,
  usePassClaimAll,
} from '../../api/hooks';
import { setCelebrationPending } from '../../celebrationGate';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
// Лого — то же, что на фоне приложения (решение владельца)
const logoUrl = '/logo.png';
import { RewardPreview } from './RewardPreview';
import type { PassCell } from '../../api/types';

const reducedMotion = () =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 6.2b — «Straddle Pass+ активирован»: полноэкранная сцена с частицами
 * (canvas, 30 шт.) и золотой вспышкой (CSS), сетка ретро-наград появляется
 * по ~120 мс, легендарная последней со вспышкой. Показывается по вотермарку
 * plus_celebrated_at; встаёт в очередь celebrationGate.
 */
export function PlusCelebration({ enabled }: { enabled: boolean }) {
  const flags = useFlags();
  const flagOn = Boolean(flags.data?.pass);
  const pass = usePass(enabled && flagOn);
  const ack = useAckPlusCelebration();
  const claimAll = usePassClaimAll();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [shown, setShown] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const cells = useMemo(() => {
    const me = pass.data?.me;
    if (!me) return [] as PassCell[];
    const list = (pass.data?.tracks ?? [])
      .filter((t) => t.level <= me.level && t.plus)
      .map((t) => t.plus as PassCell);
    // Легендарная — последней (спека 6.2b).
    return [...list].sort((a, b) => rarityOf(a) - rarityOf(b) || a.level - b.level);
  }, [pass.data]);

  const visible = Boolean(
    enabled && flagOn && !dismissed && pass.data?.me?.plusCelebration,
  );
  useEffect(() => {
    setCelebrationPending('pass-plus', visible);
    return () => setCelebrationPending('pass-plus', false);
  }, [visible]);

  // Частицы (спека: 30 шт., всплывают, мерцают)
  useEffect(() => {
    if (!visible) return;
    haptics.success();
    track('pass_plus_celebration', { rewards: cells.length });
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = (canvas.width = window.innerWidth);
    const H = (canvas.height = window.innerHeight);
    const parts = Array.from({ length: 30 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: 0.6 + Math.random() * 1.6,
      v: 0.1 + Math.random() * 0.3,
      phi: Math.random() * Math.PI * 2,
    }));
    let raf = 0;
    const draw = (t: number) => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        p.y -= p.v;
        p.x += Math.sin(t * 0.0007 + p.phi) * 0.15;
        if (p.y < -4) {
          p.y = H + 4;
          p.x = Math.random() * W;
        }
        ctx.globalAlpha = (0.45 + 0.55 * Math.abs(Math.sin(t * 0.0015 + p.phi))) * 0.85;
        ctx.fillStyle = '#E9C874';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reducedMotion()) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [visible, cells.length]);

  // Появление ячеек по ~120 мс; heavy и полноэкранная вспышка — в момент
  // легендарной (спека), а не по таймеру.
  const [flashAt, setFlashAt] = useState(0);
  useEffect(() => {
    if (!visible) return;
    if (reducedMotion()) {
      setShown(cells.length);
      return;
    }
    setShown(0);
    const timers: number[] = [];
    cells.forEach((c, i) => {
      timers.push(
        window.setTimeout(() => {
          setShown(i + 1);
          if (rarityOf(c) >= 3) {
            haptics.heavy();
            setFlashAt(Date.now());
          }
        }, 400 + i * 120),
      );
    });
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [visible, cells]);

  if (!visible || !pass.data?.me || !pass.data.season) return null;
  const { me, season } = pass.data;

  const finish = (claim: boolean) => {
    haptics.tap();
    void ack.mutateAsync().catch(() => undefined);
    if (claim) {
      void claimAll
        .mutateAsync()
        .then(() => haptics.success())
        .catch(() => undefined);
    }
    setDismissed(true);
    if (!claim) navigate('/pass');
  };

  return (
    <div className="bp-plusfx" data-theme="dark">
      <canvas ref={canvasRef} className="bp-plusfx-particles" aria-hidden />
      {flashAt > 0 && <div key={flashAt} className="bp-plusfx-flash bp-plusfx-flash--once" aria-hidden />}
      <div className="bp-plusfx-rays" aria-hidden />
      <div className="bp-plusfx-content">
        <div className="bp-plusfx-logo">
          <img src={logoUrl} alt="" />
        </div>
        <div className="bp-hero-label">STRADDLE PASS+ · {season.name.toUpperCase()}</div>
        <h2>Straddle Pass+ активирован</h2>
        {cells.length > 0 && (
          <>
            <p>
              Открыли {cells.length} наград дорожки Pass+ за уже пройденные{' '}
              {me.level} {me.level === 1 ? 'уровень' : me.level < 5 ? 'уровня' : 'уровней'}
            </p>
            <div className="bp-plusfx-grid">
              {cells.map((c, i) => {
                const r = rarityOf(c);
                return (
                  <div
                    key={`${c.level}`}
                    className={`bp-plusfx-cell${i < shown ? ' in' : ''}${r >= 3 && i < shown ? ' legend' : ''}`}
                  >
                    {/* живое превью предмета, как в гардеробе/на дорожке */}
                    <RewardPreview cell={c} size={52} active />
                    <span className="lvl mono">{c.level}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <button className="btn bp-claim-all" onClick={() => finish(true)}>
          Забрать всё
        </button>
        <button className="bp-plusfx-skip" onClick={() => finish(false)}>
          К Straddle Pass
        </button>
      </div>
    </div>
  );
}

function rarityOf(c: PassCell): number {
  return c.kind === 'deck' ? (c.deckRarity ?? 0) : (c.item?.rarity ?? 0);
}
